import { useRef, useState } from 'react';
import EmojiPicker from '../EmojiPicker';
import { PlusIcon, SmileIcon } from '../Icons';
import ChatAttachMenu from './ChatAttachMenu';
import QuotePreviewBar from './QuotePreviewBar';
import type { ReplyTarget } from '../../types/chat';

interface ChatInputComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  replyingTo?: ReplyTarget | null;
  onDismissReply?: () => void;
  insertEmoji: (emoji: string) => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
  showAttachMenu?: boolean;
  onPoll?: () => void;
  onGameInvite?: () => void;
  gameInviteTitle?: string;
  gameInviteSubtitle?: string;
  gameInviteDisabled?: boolean;
  onCiaRdv?: () => void;
  ciaRdvTitle?: string;
  ciaRdvSubtitle?: string;
}

export default function ChatInputComposer({
  value,
  onChange,
  onSend,
  onKeyDown,
  placeholder = 'Tapez un message...',
  disabled = false,
  replyingTo,
  onDismissReply,
  insertEmoji,
  inputRef: externalInputRef,
  showAttachMenu = false,
  onPoll,
  onGameInvite,
  gameInviteTitle,
  gameInviteSubtitle,
  gameInviteDisabled,
  onCiaRdv,
  ciaRdvTitle,
  ciaRdvSubtitle,
}: ChatInputComposerProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachMenuOpen, setShowAttachMenuOpen] = useState(false);
  const attachButtonRef = useRef<HTMLButtonElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const localInputRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = externalInputRef ?? localInputRef;

  const handleEmojiSelect = (emoji: string) => {
    insertEmoji(emoji);
  };

  return (
    <div className="chat-input-area">
      {replyingTo && onDismissReply && (
        <QuotePreviewBar quote={replyingTo} onDismiss={onDismissReply} />
      )}

      <div className="chat-composer">
        {showAttachMenu && onPoll && onGameInvite && (
          <div className="chat-composer-attach-wrap">
            <button
              ref={attachButtonRef}
              type="button"
              className={`chat-composer-attach-btn ${showAttachMenuOpen ? 'active' : ''}`}
              onClick={() => {
                setShowAttachMenuOpen((open) => !open);
                setShowEmojiPicker(false);
              }}
              title="Ajouter"
              aria-label="Ajouter"
              disabled={disabled}
            >
              <PlusIcon size={24} />
            </button>
            <ChatAttachMenu
              isOpen={showAttachMenuOpen}
              onClose={() => setShowAttachMenuOpen(false)}
              anchorRef={attachButtonRef}
              onPoll={onPoll}
              onGameInvite={onGameInvite}
              gameInviteTitle={gameInviteTitle}
              gameInviteSubtitle={gameInviteSubtitle}
              gameInviteDisabled={gameInviteDisabled}
              onCiaRdv={onCiaRdv}
              ciaRdvTitle={ciaRdvTitle}
              ciaRdvSubtitle={ciaRdvSubtitle}
            />
          </div>
        )}

        <div className="chat-composer-input-wrap">
          <textarea
            ref={inputRef}
            className="chat-composer-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            rows={1}
            disabled={disabled}
          />
          <div className="chat-composer-emoji-wrap">
            <button
              ref={emojiButtonRef}
              type="button"
              className={`chat-composer-emoji-btn ${showEmojiPicker ? 'active' : ''}`}
              onClick={() => {
                setShowEmojiPicker((open) => !open);
                setShowAttachMenuOpen(false);
              }}
              title="Émojis"
              aria-label="Émojis"
              disabled={disabled}
            >
              <SmileIcon size={22} />
            </button>
            <EmojiPicker
              isOpen={showEmojiPicker}
              onClose={() => setShowEmojiPicker(false)}
              onSelect={handleEmojiSelect}
              anchorRef={emojiButtonRef}
            />
          </div>
        </div>

        <button
          type="button"
          className="chat-composer-send-btn"
          onClick={onSend}
          disabled={disabled || !value.trim()}
          title="Envoyer"
          aria-label="Envoyer"
        >
          ➤
        </button>
      </div>
    </div>
  );
}
